<template>
	<slideover
		title="Edit Note"
		:open="editing !== null"
		:actions="[
			{
				text: 'Resolve',
				method: resolveNote,
				disabled: !active || !valid || !resolveAllowed,
				icon: 'square-check',
				theme: 'info'
			},
			{
				text: 'Save',
				disabled: !valid,
				method: saveNote,
				icon: 'save'
			}
		]"
		@close="close">
		<app-input
			v-if="valid"
			v-model="editing![statusKey]![0].notes"
			name="notes"
			type="textarea" />

		<app-alert v-else-if="editing" theme="warning">Invalid data for status notes</app-alert>
	</slideover>
</template>

<script lang="ts" setup>
	import { ref, watch, computed } from 'vue'
	import { ContractItem } from '@/data/schema/contract-item'
	import ContractService from '@/services/contract'
	import useToaster from '@/stores/toaster'
	import type { StatusUrl } from '@/data/types/contract-status'
	import { statusParam } from '@/composables/contracts'
	import type { WorkspaceArea } from '@/data/types/area'
	import { contractItemStatusMap } from '@/utils/statusMaps'
	import type { ContractItemStatus } from '@/data/schema/contract-item-status'

	const api = new ContractService()
	const toaster = useToaster()
	const editing = ref<ContractItem | null>(null)

	const props = withDefaults(
		defineProps<{
			item?: ContractItem | null | undefined
			area: WorkspaceArea
			resolveAllowed?: boolean
			resolveOnSave?: boolean
		}>(),
		{
			item: undefined,
			area: 'stock-check',
			resolveAllowed: false,
			resolveOnSave: undefined
		}
	)

	const valid = computed(() => {
		return editing.value?.[statusKey.value]?.[0]
	})

	const statusKey = computed(() => {
		return contractItemStatusMap[props.area]
	})

	const active = computed(() => {
		if (!editing.value || statusParam(editing.value, 'notes_status', props.area) == null) {
			return false
		}

		return statusParam(editing.value, 'notes_status', props.area) === 2
	})

	const saveNote = () => {
		const item = editing.value
		const key = statusKey.value as keyof ContractItem
		const statuses: ContractItemStatus[] = (item![key] as ContractItemStatus[]) ?? null

		toaster.working({
			text: 'Saving note...',
			timeout: 0,
			dismissable: false
		})

		if (item && statuses && statuses.length > 0) {
			if (statuses[0]) {
				const url = getUrl()

				api.updateItemStatus(
					item.id,
					statuses[0].id,
					url,
					{
						notes: statuses[0].notes,
						notes_status: statuses[0].notes_status === null ? 2 : undefined
					},
					{
						autoResolve: props.resolveOnSave
					}
				)
					.then(() => {
						toaster.success({
							text: 'Note saved successfully'
						})
						editing.value = null
						emit('updated')
						emit('closed')
					})
					.catch((error: any) => {
						toaster.error({ text: 'Error occurred updating note' })
						console.error(error)
					})
					.then(() => {
						toaster.clear()
					})
			}
		}
	}

	const resolveNote = () => {
		const item = editing.value
		const key = statusKey.value as keyof ContractItem
		const statuses: ContractItemStatus[] = (item![key] as ContractItemStatus[]) ?? null

		toaster.working({
			text: 'Resolving note...',
			timeout: 0,
			dismissable: false
		})

		if (item && statuses && statuses.length > 0) {
			if (statuses[0]) {
				const url = getUrl()

				api.updateItemStatus(item.id, statuses[0].id, url, {
					notes: statuses[0].notes,
					notes_status: 3
				})
					.then(() => {
						toaster.success({ text: 'Note marked as resolved' })
						editing.value = null
						emit('updated')
						emit('closed')
					})
					.catch((error: any) => {
						toaster.error({ text: 'Error occurred updating note' })
						console.error(error)
					})
					.then(() => {
						toaster.clear()
					})
			}
		}
	}

	function getUrl(): StatusUrl {
		const urlMap = {
			stock_check_status: 'stock-status',
			pick_status: 'pick-status',
			load_status: 'load-status'
		}

		return (urlMap[statusKey.value] as StatusUrl) || 'stock-status'
	}

	function close() {
		editing.value = null
		emit('closed')
	}

	watch(
		() => props.item,
		(newVal) => {
			editing.value = newVal ?? null
		}
	)

	// Events
	const emit = defineEmits(['updated', 'closed'])
</script>
